import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeToolbar } from '@xyflow/react';
import useCanvasStore from '@/store/canvasStore';
import { Pencil, Trash2, Plus, X, Check } from 'lucide-react';
import LatexText from '@/components/core/LatexText';
import GhostSuggestions from './GhostSuggestions';

const MASTERY_COLORS = {
  none: 'border-gray-200',
  learning: 'border-yellow-400',
  competent: 'border-blue-500',
  mastered: 'border-green-500',
};

const MASTERY_BG = {
  none: '',
  learning: 'bg-yellow-50',
  competent: 'bg-blue-50',
  mastered: 'bg-green-50',
};

const AtlasNode = memo(({ id, data, selected }) => {
  const mode = useCanvasStore(s => s.mode);
  const deleteNode = useCanvasStore(s => s.deleteNode);
  const addNode = useCanvasStore(s => s.addNode);
  const updateNodeData = useCanvasStore(s => s.updateNodeData);

  const [showEdit, setShowEdit] = useState(false);
  const [showAddChooser, setShowAddChooser] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const mastery = data.mastery || 'none';
  const isParent = data.nodeType === 'parent';
  const isChild = data.nodeType === 'child';
  const isRoot = id === 'root';

  const showActionBar = selected && mode === 'edit' && !showEdit && !showAddChooser;

  const handleStartEdit = useCallback((e) => {
    e.stopPropagation();
    setEditLabel(data.label);
    setEditDesc(data.description || '');
    setShowEdit(true);
    setShowAddChooser(false);
  }, [data.label, data.description]);

  const handleSaveEdit = useCallback((e) => {
    e.stopPropagation();
    updateNodeData(id, { label: editLabel, description: editDesc });
    setShowEdit(false);
  }, [id, editLabel, editDesc, updateNodeData]);

  const handleCancelEdit = useCallback((e) => {
    e.stopPropagation();
    setShowEdit(false);
  }, []);

  const handleShowAdd = useCallback((e) => {
    e.stopPropagation();
    setShowAddChooser(true);
    setShowEdit(false);
  }, []);

  const handleAddNode = useCallback((e, nodeType) => {
    e.stopPropagation();
    addNode(id, 'New Topic', nodeType);
    setShowAddChooser(false);
  }, [id, addNode]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    if (id !== 'root') deleteNode(id);
  }, [id, deleteNode]);

  const handleCloseAdd = useCallback((e) => {
    e.stopPropagation();
    setShowAddChooser(false);
  }, []);

  // Close popups when node deselects
  useEffect(() => {
    if (!selected) {
      setShowEdit(false);
      setShowAddChooser(false);
    }
  }, [selected]);

  // Close popups when node deselects
  if (!selected && (showEdit || showAddChooser)) {
    setShowEdit(false);
    setShowAddChooser(false);
  }

  return (
    <div
      className={`
        bg-white border-2 ${MASTERY_COLORS[mastery]} ${MASTERY_BG[mastery]}
        transition-all duration-200
        ${isChild ? 'rounded-2xl' : 'rounded-md'}
        ${selected ? 'shadow-sharp-sm ring-1 ring-cobalt' : 'shadow-sm hover:shadow-md'}
        ${isRoot ? 'px-5 py-3' : isParent ? 'px-4 py-2.5' : 'px-2.5 py-1.5'}
      `}
      style={{ minWidth: isRoot ? 140 : isParent ? 120 : 80, maxWidth: isChild ? 200 : 240 }}
      data-testid={`atlas-node-${id}`}
    >
      <LatexText 
        text={data.label}
        className={`font-body text-center leading-tight block ${
          isRoot ? 'text-sm font-semibold' : isParent ? 'text-xs font-medium' : 'text-[11px]'
        }`}
      />
      {data.description && (
        <LatexText 
          text={data.description}
          className={`font-body text-muted-foreground text-center mt-1 leading-snug block ${
            isChild ? 'text-[9px]' : 'text-[10px]'
          }`}
        />
      )}
      {mastery !== 'none' && (
        <div className="flex justify-center mt-1.5">
          <span className={`font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            mastery === 'learning' ? 'bg-yellow-100 text-yellow-700' :
            mastery === 'competent' ? 'bg-blue-100 text-blue-700' :
            'bg-green-100 text-green-700'
          }`}>{mastery}</span>
        </div>
      )}

      {/* ── Action Bar (Edit mode) ── */}
      <NodeToolbar isVisible={showActionBar} position={Position.Bottom} offset={8}>
        <div className="flex items-center gap-0.5 bg-white border border-border rounded-lg shadow-md px-1 py-0.5" data-testid={`node-toolbar-${id}`}>
          <button onClick={handleStartEdit} className="p-1.5 hover:bg-muted rounded-md transition-colors" title="Edit" data-testid={`node-edit-btn-${id}`}>
            <Pencil className="w-3.5 h-3.5 text-foreground" strokeWidth={1.5} />
          </button>
          {id !== 'root' && (
            <button onClick={handleDelete} className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-md transition-colors" title="Delete" data-testid={`node-delete-btn-${id}`}>
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}
          <div className="w-px h-4 bg-border mx-0.5" />
          <button onClick={handleShowAdd} className="p-1.5 hover:bg-muted rounded-md transition-colors" title="Add node" data-testid={`node-add-btn-${id}`}>
            <Plus className="w-3.5 h-3.5 text-foreground" strokeWidth={1.5} />
          </button>
        </div>
      </NodeToolbar>

      {/* ── Edit Form ── */}
      <NodeToolbar isVisible={showEdit && selected && mode === 'edit'} position={Position.Bottom} offset={8}>
        <div className="bg-white border border-border rounded-lg shadow-md p-3 w-60" onMouseDown={e => e.stopPropagation()} data-testid={`node-edit-form-${id}`}>
          <label className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground mb-1 block">Title</label>
          <input
            value={editLabel}
            onChange={e => {
              setEditLabel(e.target.value);
              // Broadcast text change in real-time for collaboration
              if (window.__collabSendTextEdit) {
                window.__collabSendTextEdit(id, e.target.value);
              }
            }}
            className="w-full text-xs font-body border border-border rounded-md px-2.5 py-1.5 mb-2.5 outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt/20"
            placeholder="Node title"
            autoFocus
            data-testid={`node-edit-title-${id}`}
          />
          <label className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground mb-1 block">Description</label>
          <textarea
            value={editDesc}
            onChange={e => setEditDesc(e.target.value)}
            className="w-full text-xs font-body border border-border rounded-md px-2.5 py-1.5 mb-2.5 outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt/20 resize-none"
            placeholder="Description (optional)"
            rows={2}
            data-testid={`node-edit-desc-${id}`}
          />
          <div className="flex items-center gap-1.5 justify-end">
            <button onClick={handleCancelEdit} className="px-2.5 py-1 text-[10px] font-body hover:bg-muted rounded-md border border-border transition-colors" data-testid={`node-edit-cancel-${id}`}>
              Cancel
            </button>
            <button onClick={handleSaveEdit} className="px-2.5 py-1 text-[10px] font-body bg-cobalt text-white hover:bg-cobalt/90 rounded-md transition-colors" data-testid={`node-edit-save-${id}`}>
              Save
            </button>
          </div>
        </div>
      </NodeToolbar>

      {/* ── Add Node Chooser ── */}
      <NodeToolbar isVisible={showAddChooser && selected && mode === 'edit'} position={Position.Bottom} offset={8}>
        <div className="bg-white border border-border rounded-lg shadow-md p-2" onMouseDown={e => e.stopPropagation()} data-testid={`node-add-chooser-${id}`}>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">Add node</span>
            <button onClick={handleCloseAdd} className="p-0.5 hover:bg-muted rounded">
              <X className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={(e) => handleAddNode(e, 'parent')}
              className="flex flex-col items-center gap-1.5 p-2.5 hover:bg-muted rounded-lg border border-border transition-colors w-[72px]"
              data-testid={`add-parent-node-${id}`}>
              <div className="w-10 h-7 bg-white border-2 border-gray-300 rounded-md" />
              <span className="text-[10px] font-body font-medium">Parent</span>
            </button>
            <button onClick={(e) => handleAddNode(e, 'child')}
              className="flex flex-col items-center gap-1.5 p-2.5 hover:bg-muted rounded-lg border border-border transition-colors w-[72px]"
              data-testid={`add-child-node-${id}`}>
              <div className="w-8 h-5 bg-white border-2 border-gray-300 rounded-2xl" />
              <span className="text-[10px] font-body font-medium">Child</span>
            </button>
          </div>
        </div>
      </NodeToolbar>

      {/* ── Handles ── */}
      <Handle type="source" position={Position.Top} id="h-top" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="source" position={Position.Bottom} id="h-bottom" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="source" position={Position.Left} id="h-left" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="source" position={Position.Right} id="h-right" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="target" position={Position.Top} id="t-top" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="target" position={Position.Bottom} id="t-bottom" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="target" position={Position.Left} id="t-left" className="!w-2 !h-2 !bg-white !border-gray-300" />
      <Handle type="target" position={Position.Right} id="t-right" className="!w-2 !h-2 !bg-white !border-gray-300" />

      {/* ── AI Ghost Suggestions (Edit mode only) ── */}
      {selected && mode === 'edit' && !showEdit && !showAddChooser && (
        <GhostSuggestions
          nodeId={id}
          nodeLabel={data.label}
          nodeDescription={data.description}
        />
      )}
    </div>
  );
});

AtlasNode.displayName = 'AtlasNode';
export default AtlasNode;
